import { CircularProgress, ThemeProvider } from "@mui/material";
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Clock,
  TrendingUp,
  UserCheck,
  Users,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from "recharts";
import StatisticApi from "../../api/statisticApi";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { StatisticResponse } from "../../types/Statistic";
import { theme } from "../../utils/theme";
import s from "./Dashboard.module.css";

const Dashboard = () => {
  const token = localStorage.getItem("token");
  const navigate = useNavigate();
  const [statisticData, setStatisticData] = useState<StatisticResponse | null>(
    null
  );
  const [loader, setLoader] = useState({ mainLoader: false });
  const [activeDeclinedTab, setActiveDeclinedTab] = useState<string | null>(
    null
  );

  // Автовыбор первой вкладки при загрузке данных
  useEffect(() => {
    if (!statisticData) return;
    if (statisticData.declinedReviewsStats.length > 0 && !activeDeclinedTab) {
      setActiveDeclinedTab(
        statisticData.declinedReviewsStats[0].specialization
      );
    }
  }, [statisticData, activeDeclinedTab]);

  useEffect(() => {
    if (!token) return;
    const makeAsync = async () => {
      setLoader((prev) => ({ ...prev, mainLoader: true }));
      const response = await StatisticApi.getAdminDashboardStats(token);

      setLoader((prev) => ({ ...prev, mainLoader: false }));
      if (response.status && response.statistics) {
        setStatisticData(response.statistics);
      }
    };
    makeAsync();
  }, [token]);

  if (loader.mainLoader)
    return (
      <div className={s.mainLoaderContainer}>
        <ThemeProvider theme={theme}>
          <CircularProgress size={150} color="primary" />
        </ThemeProvider>
      </div>
    );

  if (!statisticData) return null;

  // Chart colors for dark theme
  const colors = {
    primary: "#3bb182",
    secondary: "#ff6a69",
    accent: "#48566a",
    success: "#10b981",
    warning: "#f59e0b",
    info: "#3b82f6",
    purple: "#a78bfa",
    teal: "#14b8a6",
  };

  // Prepare data for charts
  const userDistributionData = [
    {
      name: "Clients",
      value: statisticData.defaultUsersAmount,
      color: colors.info,
      fill: colors.info,
    },
    {
      name: "Specialists",
      value: statisticData.specialistUsersAmount,
      color: colors.primary,
      fill: colors.primary,
    },
  ];

  const profileStatusData = [
    {
      name: "Active",
      value: statisticData.activeProfiles,
      color: colors.success,
      fill: colors.success,
    },
    {
      name: "Under Review",
      value: statisticData.profilesReadyToReview,
      color: colors.warning,
      fill: colors.warning,
    },
  ];

  const completionRate =
    statisticData.sessionsAmount > 0
      ? Math.round(
          (statisticData.completedSessionsAmount /
            statisticData.sessionsAmount) *
            100
        )
      : 0;

  const userMetricsData = [
    {
      category: "Users",
      clients: statisticData.defaultUsersAmount,
      specialists: statisticData.specialistUsersAmount,
    },
  ];

  const RadialTooltip = ({
    active,
    payload,
    label,
  }: TooltipContentProps<string | number, string>) => {
    const isVisible = active && payload && payload.length;

    return (
      <div
        className="custom-tooltip"
        style={{
          visibility: isVisible ? "visible" : "hidden",
          backgroundColor: "#404040",
          border: "none",
          borderRadius: "8px",
          color: "white",
          padding: "20px",
        }}
      >
        <p>Completed sessions: {statisticData.completedSessionsAmount}</p>
        <p>Total sessions: {statisticData.sessionsAmount}</p>
      </div>
    );
  };

  return (
    <div className={s.container}>
      {/* Header */}
      <div style={{ marginBottom: "32px" }}>
        <h1
          style={{
            fontSize: "32px",
            fontWeight: "bold",
            marginBottom: "8px",
            color: "white",
            margin: 0,
          }}
        >
          Dashboard
        </h1>
        <p style={{ color: "#727272", fontSize: "14px", margin: "8px 0 0 0" }}>
          Overview of your admin panel statistics
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader style={{ paddingBottom: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <CardTitle
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#909090",
                }}
              >
                Total Clients
              </CardTitle>
              <Users size={20} style={{ color: colors.info }} />
            </div>
          </CardHeader>
          <CardContent>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "white" }}
            >
              {statisticData.defaultUsersAmount.toLocaleString()}
            </div>
            <p
              style={{
                fontSize: "12px",
                color: colors.success,
                marginTop: "8px",
              }}
            >
              <TrendingUp
                size={14}
                style={{ display: "inline", marginRight: "4px" }}
              />
              Active users
            </p>
          </CardContent>
        </Card>

        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader style={{ paddingBottom: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <CardTitle
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#909090",
                }}
              >
                Total Specialists
              </CardTitle>
              <UserCheck size={20} style={{ color: colors.primary }} />
            </div>
          </CardHeader>
          <CardContent>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "white" }}
            >
              {statisticData.specialistUsersAmount.toLocaleString()}
            </div>
            <p style={{ fontSize: "12px", color: "#909090", marginTop: "8px" }}>
              {statisticData.specialistProfilesAmount} profiles total
            </p>
          </CardContent>
        </Card>

        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader style={{ paddingBottom: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <CardTitle
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#909090",
                }}
              >
                Total Sessions
              </CardTitle>
              <Calendar size={20} style={{ color: colors.warning }} />
            </div>
          </CardHeader>
          <CardContent>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "white" }}
            >
              {statisticData.sessionsAmount.toLocaleString()}
            </div>
            <p style={{ fontSize: "12px", color: "#909090", marginTop: "8px" }}>
              {statisticData.completedSessionsAmount} completed
            </p>
          </CardContent>
        </Card>

        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader style={{ paddingBottom: "8px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <CardTitle
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#909090",
                }}
              >
                Avg Duration
              </CardTitle>
              <Clock size={20} style={{ color: colors.purple }} />
            </div>
          </CardHeader>
          <CardContent>
            <div
              style={{ fontSize: "32px", fontWeight: "bold", color: "white" }}
            >
              {Math.round(statisticData.sessionsAverageDuration)}
            </div>
            <p style={{ fontSize: "12px", color: "#909090", marginTop: "8px" }}>
              minutes per session
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Review Queue Reminder Widget */}
      {statisticData && statisticData.profilesReadyToReview > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <Card
            style={{
              backgroundColor: "#1a1a1a",
              border: `2px solid ${colors.warning}`,
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => navigate("/review-specialists")}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = `0 8px 24px ${colors.warning}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <CardContent style={{ padding: "24px" }}>
              {/* Заголовок + общее количество */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "20px" }}
                >
                  <div
                    style={{
                      width: "64px",
                      height: "64px",
                      borderRadius: "12px",
                      background: `linear-gradient(135deg, ${colors.warning}33, ${colors.warning}22)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: `2px solid ${colors.warning}`,
                    }}
                  >
                    <AlertCircle size={32} style={{ color: colors.warning }} />
                  </div>

                  <div>
                    <h3
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "white",
                        margin: 0,
                      }}
                    >
                      {statisticData.profilesReadyToReview} Profile
                      {statisticData.profilesReadyToReview !== 1
                        ? "s"
                        : ""}{" "}
                      Awaiting Review
                    </h3>
                    <p
                      style={{
                        color: "#909090",
                        fontSize: "14px",
                        margin: "8px 0 0 0",
                      }}
                    >
                      Specialists are waiting for profile approval. Click to
                      start reviewing.
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "16px 24px",
                    backgroundColor: `${colors.warning}22`,
                    borderRadius: "8px",
                    border: `1px solid ${colors.warning}44`,
                  }}
                >
                  <span
                    style={{
                      color: colors.warning,
                      fontSize: "16px",
                      fontWeight: "600",
                    }}
                  >
                    Review Now
                  </span>
                  <ArrowRight size={20} style={{ color: colors.warning }} />
                </div>
              </div>

              {/* Разбивка по специализациям */}
              {statisticData.reviewStatsBySpecialization.length > 0 && (
                <div
                  style={{
                    padding: "20px",
                    backgroundColor: "#252525",
                    borderRadius: "12px",
                    border: `1px solid ${colors.warning}44`,
                  }}
                >
                  <p
                    style={{
                      color: "#b0b0b0",
                      fontSize: "14px",
                      margin: "0 0 16px 0",
                      fontWeight: "600",
                    }}
                  >
                    Breakdown by specialization:
                  </p>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                    }}
                  >
                    {statisticData.reviewStatsBySpecialization.map((item) => (
                      <div
                        key={item.specialization}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "12px 16px",
                          backgroundColor: "#2a2a2a",
                          borderRadius: "8px",
                        }}
                      >
                        <span style={{ color: "white", fontWeight: "500" }}>
                          {item.specialization}
                        </span>

                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                          }}
                        >
                          {/* Новые (first-time) */}
                          {item.firstTimeUnderReview > 0 && (
                            <div style={{ textAlign: "center" }}>
                              <span
                                style={{
                                  color: colors.warning,
                                  fontWeight: "700",
                                  fontSize: "18px",
                                }}
                              >
                                {item.firstTimeUnderReview}
                              </span>
                              <span
                                style={{
                                  color: "#909090",
                                  fontSize: "12px",
                                  marginLeft: "4px",
                                }}
                              >
                                new
                              </span>
                            </div>
                          )}

                          {/* Общее количество на модерации */}
                          <span style={{ color: "#e0e0e0", fontWeight: "600" }}>
                            {item.firstTimeUnderReview > 0 && " / "}
                            {item.totalUnderReview} total
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* === НОВЫЙ ОТДЕЛЬНЫЙ БЛОК: Статистика по отклонённым профилям === */}
      {statisticData && statisticData.declinedReviewsStats.length > 0 && (
        <div style={{ marginBottom: "30px" }}>
          <Card
            style={{
              backgroundColor: "#1a1a1a",
            }}
          >
            <CardContent style={{ padding: "24px" }}>
              <h3
                style={{
                  color: "white",
                  fontSize: "20px",
                  fontWeight: "700",
                  margin: "0 0 20px 0",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <AlertTriangle
                  size={24}
                  style={{ color: "rgb(16, 185, 129)" }}
                />
                Statistics by specializations
              </h3>

              {/* Табы — по одной вкладке на специализацию */}
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  marginBottom: "20px",
                }}
              >
                {statisticData.declinedReviewsStats.map((item, index) => (
                  <button
                    key={item.specialization}
                    style={{
                      padding: "10px 18px",
                      backgroundColor:
                        activeDeclinedTab === item.specialization
                          ? "rgb(16, 185, 129)"
                          : "#2a2a2a",
                      color: "white",
                      border: "none",
                      borderRadius: "8px",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      whiteSpace: "nowrap",
                    }}
                    onClick={() => setActiveDeclinedTab(item.specialization)}
                  >
                    {item.specialization} ({item.amount})
                  </button>
                ))}
              </div>

              {/* Контент выбранной вкладки */}
              {activeDeclinedTab && (
                <div
                  style={{
                    padding: "20px",
                    backgroundColor: "#252525",
                    borderRadius: "12px",
                    border: "1px dashed #ff444466",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "16px",
                        backgroundColor: "#ff444422",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: "2px solid #ff4444",
                      }}
                    >
                      <XCircle size={40} style={{ color: "#ff4444" }} />
                    </div>

                    <div>
                      <h4
                        style={{
                          color: "white",
                          margin: "0 0 8px 0",
                          fontSize: "18px",
                          fontWeight: "600",
                        }}
                      >
                        {activeDeclinedTab}
                      </h4>
                      <p
                        style={{
                          color: "#ff6666",
                          fontSize: "28px",
                          fontWeight: "700",
                          margin: "0",
                        }}
                      >
                        {
                          statisticData.declinedReviewsStats.find(
                            (d) => d.specialization === activeDeclinedTab
                          )?.amount
                        }{" "}
                        declined profile
                        {statisticData.declinedReviewsStats.find(
                          (d) => d.specialization === activeDeclinedTab
                        )?.amount !== 1
                          ? "s"
                          : ""}
                      </p>
                      <p
                        style={{
                          color: "#909090",
                          fontSize: "14px",
                          margin: "8px 0 0 0",
                        }}
                      >
                        These specialists have been rejected and are waiting for
                        correction or re-submission.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(450px, 1fr))",
          gap: "20px",
          marginBottom: "30px",
        }}
      >
        {/* User Distribution Pie Chart */}
        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader>
            <CardTitle style={{ color: "white", fontSize: "18px" }}>
              User Distribution
            </CardTitle>
            <CardDescription style={{ color: "#909090" }}>
              Breakdown of clients vs specialists
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={userDistributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  dataKey="value"
                >
                  {userDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#404040",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                  }}
                  itemStyle={{ color: "white" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Session Completion Rate - Radial */}
        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader>
            <CardTitle style={{ color: "white", fontSize: "18px" }}>
              Session Completion Rate
            </CardTitle>
            <CardDescription style={{ color: "#909090" }}>
              Percentage of completed sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="60%"
                outerRadius="100%"
                barSize={20}
                startAngle={90}
                endAngle={-270}
                data={[
                  {
                    name: "Rate",
                    full: 100,
                    completed: completionRate,
                  },
                ]}
              >
                {/* Фон (весь круг) */}
                <RadialBar dataKey="full" cornerRadius={10} fill="#404040" />

                {/* Основная дуга (прогресс) */}
                <RadialBar
                  dataKey="completed"
                  cornerRadius={10}
                  fill={colors.primary}
                />

                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{
                    fontSize: "48px",
                    fontWeight: "bold",
                    fill: "white",
                  }}
                >
                  {completionRate}%
                </text>

                <Tooltip
                  contentStyle={{
                    backgroundColor: "#404040",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                  }}
                  content={RadialTooltip}
                />
              </RadialBarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Profile Status Distribution */}
        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader>
            <CardTitle style={{ color: "white", fontSize: "18px" }}>
              Profile Status
            </CardTitle>
            <CardDescription style={{ color: "#909090" }}>
              Specialist profiles by status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={profileStatusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) =>
                    `${name}: ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  dataKey="value"
                >
                  {profileStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#404040",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                  }}
                  itemStyle={{ color: "white" }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Users Bar Chart */}
        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader>
            <CardTitle style={{ color: "white", fontSize: "18px" }}>
              User Metrics
            </CardTitle>
            <CardDescription style={{ color: "#909090" }}>
              Clients and specialists comparison
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={userMetricsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                <XAxis dataKey="category" stroke="#909090" />
                <YAxis stroke="#909090" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#404040",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                  }}
                />
                <Legend />
                <Bar
                  dataKey="clients"
                  fill={colors.info}
                  name="Clients"
                  radius={[8, 8, 0, 0]}
                />
                <Bar
                  dataKey="specialists"
                  fill={colors.primary}
                  name="Specialists"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div>
        <Card
          style={{ backgroundColor: "#282828", border: "none", color: "white" }}
        >
          <CardHeader>
            <CardTitle style={{ color: "white", fontSize: "18px" }}>
              Quick Statistics
            </CardTitle>
            <CardDescription style={{ color: "#909090" }}>
              Key metrics at a glance
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "16px",
              }}
            >
              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#404040",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${colors.info}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#909090",
                    marginBottom: "4px",
                  }}
                >
                  Total Profiles
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {statisticData.specialistProfilesAmount}
                </div>
              </div>

              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#404040",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${colors.success}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#909090",
                    marginBottom: "4px",
                  }}
                >
                  Active Profiles
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {statisticData.activeProfiles}
                </div>
              </div>

              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#404040",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${colors.warning}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#909090",
                    marginBottom: "4px",
                  }}
                >
                  Ready to Review
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {statisticData.profilesReadyToReview}
                </div>
              </div>

              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#404040",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${colors.primary}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#909090",
                    marginBottom: "4px",
                  }}
                >
                  Completion Rate
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {completionRate}%
                </div>
              </div>

              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#404040",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${colors.purple}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#909090",
                    marginBottom: "4px",
                  }}
                >
                  Avg Session (min)
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {Math.round(statisticData.sessionsAverageDuration)}
                </div>
              </div>

              <div
                style={{
                  padding: "16px",
                  backgroundColor: "#404040",
                  borderRadius: "8px",
                  borderLeft: `4px solid ${colors.teal}`,
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#909090",
                    marginBottom: "4px",
                  }}
                >
                  Total Users
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  {(
                    statisticData.defaultUsersAmount +
                    statisticData.specialistUsersAmount
                  ).toLocaleString()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
